import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useAuth } from '@/contexts/AuthContext'
import { usersService, UpdateUserPayload } from '@/services/usersService'
import { UserModel } from '@/models/user.model'

type UpdatePayload = Pick<UpdateUserPayload, 'firstName' | 'lastName' | 'phone' | 'bio'>

export function useCurrentUserProfile() {
  const { user: authUser, updateUser: syncUser } = useAuth()
  const queryClient = useQueryClient()

  const profileQuery = useQuery({
    queryKey: ['users', 'profile'],
    queryFn: async () => {
      const profile = await usersService.getCurrentUserProfile()
      syncUser(profile)
      return profile
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  })

  const updateMutation = useMutation({
    mutationFn: async (payload: UpdatePayload) => {
      return usersService.updateCurrentUserProfile(payload)
    },
    onSuccess: (updatedUser: UserModel) => {
      if (updatedUser.id) {
        queryClient.setQueryData(['users', 'profile'], updatedUser)
        queryClient.setQueryData(['users', 'detail', updatedUser.id], updatedUser)
      }
      syncUser(updatedUser)
    },
  })

  const profile = useMemo(() => {
    if (profileQuery.data) {
      return profileQuery.data
    }
    return authUser ? UserModel.fromJSON(authUser) : null
  }, [authUser, profileQuery.data])

  return {
    profile,
    userId: profile?.id ?? null,
    ...profileQuery,
    updateProfile: updateMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    updateError: updateMutation.error,
  }
}
